import {
  FormGroupContext,
  type FormGroupContextField,
  type FormGroupContextFieldInfo,
  type FormGroupContextProps
} from "@common/contexts/FormGroup";
import { updateItemBy } from "@common/lib/array";
import { Common } from "@common/types";
import classNames from "classnames";
import React from "react";
import { recalculateColSpan } from "./lib";

type Props = Common.WithChildren & {
  className?: string;
  hint?: string;
  style?: React.CSSProperties;
};
const FormGroup = (props: Props) => {
  const [fields, setFields] = React.useState<FormGroupContextField[]>([]);
  const contextValue = React.useMemo(
    (): FormGroupContextProps => ({
      get fields() {
        return fields;
      },
      register: (name: string) => {
        setFields((x) => recalculateColSpan([...x, { name, colSpan: 0 }]));
      },
      patch: (name: string, info: FormGroupContextFieldInfo) => {
        setFields((x) => {
          const res = updateItemBy(
            x,
            (y) => y.name === name,
            (y) => ({ ...y, info, colSpan: info.colSpan ?? 0 })
          );

          return recalculateColSpan(res);
        });
      },
      unregister: (name: string) => {
        setFields((x) => recalculateColSpan(x.filter((y) => y.name !== name)));
      }
    }),
    [fields]
  );

  React.useEffect(() => {
    const check = (children: any) =>
      React.Children.forEach(children, (x) => {
        if (!x) return x;

        if (x.type === FormGroup) throw new Error("BUG. Form group cannot have nested form groups");
        if (x.props?.children) check(x.props.children);
      });

    check(props.children);
  }, [props.children]);

  return (
    <FormGroupContext.Provider value={contextValue}>
      <div className={classNames("form_group row", props.className)}>
        <div className="content">{props.children}</div>

        {!!props.hint && <div className="hint">{props.hint}</div>}
      </div>
    </FormGroupContext.Provider>
  );
};
export default FormGroup;
